<template>
  <div v-if="event" class="bg-white shadow-lg w-full mb-5 flex flex-wrap p-5">
    <div class="w-full md:w-3/12 lg:w-1/6 relative">
      <strong class="block leading-tightest mb-1 pr-10 md:pr-5 fluid-sm tracking-wider font-medium">{{
        event.title
      }}</strong>
      <span class="inline-block bg-br-green-light rounded-full px-5 py-2 mt-1 fluid-xs leading-tightest font-medium">{{
        $t('education.' + event.attendance)
      }}</span>
      <div class="absolute right-0 top-0 p-3 block md:hidden bg-putty rounded-full my-3" @click="open = !open">
        <DownChevronIcon :down="open" class="block w-3 h-3" />
      </div>
    </div>
    <Expandable
      v-if="event.schedule && event.schedule.length"
      :open="open"
      class="w-full md:w-9/12 lg:w-5/6 mt-5 md:mt-0 lg:pl-5"
    >
      <div
        v-for="schedule in event.schedule"
        :key="schedule.id"
        class="schedule-col grid grid-cols-12 gap-3 md:gap-10 grid-rows-3 md:grid-rows-1"
      >
        <div v-if="schedule.start" class="schedule-row col-start-1 col-end-6 md:col-end-3">
          <strong class="block fluid-sm leading-tightest font-medium mb-1">{{ $t('education.start') }}</strong>
          <span>{{ formatDate(new Date(schedule.start), 'long') }}</span>
        </div>
        <div v-else class="mt-0 md:mt-4 col-start-1 col-end-13 md:col-end-5">{{ $t('education.learnAnytime') }}</div>
        <div v-if="schedule.end" class="schedule-row col-start-6 md:col-start-3 col-end-13 md:col-end-5">
          <strong class="block fluid-sm leading-tightest font-medium mb-1">{{ $t('education.end') }}</strong>
          <span>{{ formatDate(new Date(schedule.end), 'long') }}</span>
        </div>
        <div
          v-if="event.locationName || event.address"
          class="schedule-row row-start-2 col-start-1 col-end-6 md:col-start-5 md:col-end-8"
        >
          <strong class="block fluid-sm leading-tightest font-medium mb-1">{{ $t('education.location') }}</strong>
          <span v-if="event.locationName">{{ event.locationName }}</span>
          <span v-if="event.address">{{ event.address }}</span>
        </div>
        <!-- <div class="col-start-6 col-start-1" />-->
        <div
          class="schedule-row row-start-2 col-end-13 md:col-start-8 md:col-end-10"
          :class="event.locationName || event.address ? 'col-start-6' : 'col-start-1'"
        >
          <strong class="block fluid-sm leading-tightest font-medium mb-1">{{ $t('education.price') }}</strong>
          <span>{{
            $n(parseFloat(schedule.price), {
              style: 'currency',
              currency: currency,
            })
          }}</span>
          <em
            v-if="schedule.compareAtPrice"
            class="text-gray-light line-through fluid-sm leading-none block not-italic"
            >{{
              $n(parseFloat(schedule.compareAtPrice), {
                style: 'currency',
                currency: currency,
              })
            }}</em
          >
          <span class="fluid-xs leading-tightest text-gray-light">{{ $t('vatIncl') }}</span>
        </div>
        <div class="schedule-row row-start-3 col-start-1 col-end-13 md:col-start-10 md:col-end-13">
          <BookEventButton :schedule="schedule" />
        </div>
      </div>
    </Expandable>
  </div>
</template>
<script setup>
import Expandable from '@/components/common/Expandable';
import DownChevronIcon from '@/components/icons/DownChevronIcon';
import BookEventButton from '@/components/education/BookEventButton';

const props = defineProps({
  event: {
    type: Object,
    required: true,
  },
  currencyCode: {
    type: String,
    default: null,
  },
});

const { formatDate } = useFormatDate();
const open = ref(false);

const currency = computed(() => {
  if (props.currencyCode) {
    return props.currencyCode;
  }

  // In Storybook, getCurrentLocaleConfig might not be available
  try {
    const { $getCurrentLocaleConfig } = useNuxtApp();
    return $getCurrentLocaleConfig()?.currency || 'EUR';
  } catch (e) {
    return 'EUR';
  }
});

const hideMobileContent = () => {
  open.value = window.innerWidth >= 768;
};

onMounted(() => {
  hideMobileContent();
  window.addEventListener('resize', hideMobileContent);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', hideMobileContent);
});
</script>
<style>
@reference "~/assets/css/main.css";
.schedule-col {
  @apply border-t border-gray-300 py-5;
  &:first-child {
    @apply border-none pt-0;
  }
  &:last-child {
    @apply pb-0;
  }
}
.schedule-row {
  @apply md:row-start-1;
  & > span {
    @apply block fluid-base leading-relaxed font-light;
  }
}
.cta-button {
  @apply min-w-0 whitespace-nowrap md:mt-2 w-full;
}
</style>
